import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/legal';

/**
 * Strategiefelder - Fokus auf Absicherung (§34d-konform)
 * KEINE: ETF-Depots, Kapitalanlageimmobilien, Steueroptimierung
 */

const Strategiefelder = () => {
  const navigate = useNavigate();

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  const fields = [
    {
      id: '01',
      title: 'Berufsunfähigkeit',
      text: 'Die Absicherung deiner Arbeitskraft ist das Fundament. Ohne Einkommen funktioniert keine Strategie. Hier analysieren wir, wie groß deine Lücke wirklich ist.'
    },
    {
      id: '02',
      title: 'Krankentagegeld',
      text: 'Was passiert in den ersten Wochen einer Krankheit? Das gesetzliche Krankengeld reicht oft nicht aus. Besonders Selbstständige und Besserverdiener sind betroffen.'
    },
    {
      id: '03',
      title: 'Risikolebensversicherung',
      text: 'Wenn du Verantwortung für andere trägst (Partner, Kinder, gemeinsame Kredite), dann ist diese Absicherung nicht optional, sondern essenziell.'
    },
    {
      id: '04',
      title: 'Private Altersvorsorge',
      text: 'Wie aus regelmäßigen Beiträgen langfristig Sicherheit im Alter entsteht. Mit Fondspolicen und privaten Rentenversicherungen, die zu deiner Situation passen.'
    },
    {
      id: '05',
      title: 'Absicherungsstrategie',
      text: 'Alle Bausteine müssen zusammenspielen. Hier geht es darum, Prioritäten zu setzen und einen Plan zu entwickeln, der zu deinem Leben passt.'
    }
  ];

  return (
    <div className="min-h-screen bg-navy font-manrope">
      <Navigation variant="dark" />

      {/* HERO */}
      <section className="pt-28 sm:pt-36 md:pt-44 pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-cobalt text-xs font-medium uppercase tracking-[0.2em] mb-4">
              Übersicht
            </p>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
              Absicherungsfelder
            </h1>
            <p className="text-xl sm:text-2xl text-white/50 leading-relaxed">
              Worum es bei der Absicherung wirklich geht
            </p>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="text-center space-y-6">
            <p className="text-lg sm:text-xl text-white/70 leading-relaxed">
              Absicherung ist kein Selbstzweck. Sie schützt das, was du aufbaust.
            </p>
            <p className="text-base sm:text-lg text-white/50 leading-[1.8]">
              Die folgenden Bereiche greifen ineinander und entfalten ihre 
              Wirkung erst im Zusammenspiel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CORE IDEA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="text-center">
            <div className="inline-block">
              <div className="w-12 h-px bg-cobalt/40 mx-auto mb-10" />
            </div>
            <p className="font-playfair text-xl sm:text-2xl md:text-3xl text-white/80 leading-[1.5]">
              Es geht nicht um möglichst viele Policen.
            </p>
            <p className="font-playfair text-xl sm:text-2xl md:text-3xl text-cobalt/70 leading-[1.5] mt-3">
              Sondern um die richtige Absicherung zur richtigen Zeit.
            </p>
            <div className="inline-block">
              <div className="w-12 h-px bg-cobalt/40 mx-auto mt-10" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* STRATEGY FIELDS */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <div className="space-y-16 sm:space-y-24">
            {fields.map((field, index) => (
              <motion.div
                key={field.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="relative"
              >
                {/* Subtle connector line */}
                {index < fields.length - 1 && (
                  <div className="absolute left-4 top-16 bottom-0 w-px bg-gradient-to-b from-white/10 to-transparent hidden sm:block" style={{ height: 'calc(100% + 6rem)' }} />
                )}
                
                <div className="flex gap-6 sm:gap-8">
                  {/* Number indicator */}
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-cobalt/10 border border-cobalt/20 flex items-center justify-center">
                      <span className="text-cobalt/70 text-xs font-mono">{field.id}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pt-0.5">
                    <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-white mb-4">
                      {field.title}
                    </h3>
                    <p className="text-base text-white/50 leading-[1.8]">
                      {field.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn}>
            <div className="border-t border-white/10 pt-16 sm:pt-20">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">
                Nächster Schritt
              </p>
              <h2 className="font-playfair text-3xl sm:text-4xl font-semibold text-white mb-5">
                Finde deine Lücke
              </h2>
              <p className="text-base sm:text-lg text-white/50 mb-10 max-w-md">
                Im kostenlosen Erstgespräch schauen wir gemeinsam, wo du stehst 
                und welche Absicherung Priorität hat.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/strategiegespraech')}
                  className="group px-6 py-3.5 bg-cobalt text-white rounded-lg text-sm font-medium hover:bg-cobalt/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="strategiefelder-booking-btn"
                >
                  Gespräch vereinbaren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/absicherungscheck')}
                  className="group px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-lg text-sm font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                >
                  Absicherungs-Check starten
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default Strategiefelder;
